import BiMessageError from '../icons/BiMessageError';
import { Button, Form, Modal } from 'react-bootstrap';
import { CustomerContext } from './context/CustomerContext';
import { Link } from 'react-router-dom';
import { number, oneOfType, string } from 'prop-types';
import React, { useContext, useRef, useState } from 'react';
import { ToastContext } from './context/ToastContext';


const ReportProductIssueModal = ({ productId }) => {
    const { customerData } = useContext(CustomerContext);
    const { sendMessage } = useContext(ToastContext);

    const formRef = useRef(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [otherReason, setOtherReason] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const [show, setShow] = useState(false);
    const [validated, setValidated] = useState(false);

    const handleClose = () => setShow(false);

    const handleOptionChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleOtherReasonChange = (event) => {
        setOtherReason(event.target.value);
    };

    const handleShow = () => {
        if (Object.keys(customerData).length === 0) {
            return sendMessage(
                'You need to be logged in to report an issue.',
                'danger',
            );
        }
        setShow(true);
    };

    const handleSubmit = async (event) => {
        try {
            setIsSubmitting(true);
            event.preventDefault();
            setValidated(true);
            if (!formRef.current) {
                return;
            }
            if (formRef.current.checkValidity() === false) {
                event.stopPropagation();
                return;
            }
            if (selectedOption === 'OTHER' && otherReason.trim().length < 1) {
                return;
            }
            const payload = {
                reason: selectedOption,
                ...selectedOption === 'OTHER' && { otherReason: otherReason.trim() },
            };
            const url = new URL(`/api/products/${productId}/productIssues`, process.env.APP_URL);
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (data.error) {
                throw new Error(data.error);
            }
            if (data.success !== true) {
                throw new Error(
                    'There was a problem with the submission of your report. '
                    + 'Please try again later.'
                );
            }
            sendMessage('Your report has been received. Thank you.', 'success');
            setOtherReason('');
            setSelectedOption('');
            setValidated(false);
            setShow(false);
        } catch (error) {
            sendMessage(error.message, 'danger');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (<>
        <div
            style={{
                alignItems: 'center',
                display: 'flex',
                flexFlow: 'row wrap',
                gap: '0.25rem',
                marginBottom: '1rem',
            }}>
            <BiMessageError />
            <Link
                onClick={handleShow}>Report an issue with this product.</Link>
        </div>
        <Modal
            centered={true}
            show={show}
            onHide={handleClose}>
            <Form
                ref={formRef}
                noValidate
                validated={validated}
                onSubmit={handleSubmit}>
                <Modal.Header
                    closeButton>
                    <Modal.Title>Report product issue</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group
                        controlId={'formProductIssueReason'}>
                        <Form.Label>
                            Please tell us about the issue:
                        </Form.Label>
                        <Form.Select
                            required={true}
                            value={selectedOption}
                            onChange={handleOptionChange}>
                            <option
                                value={''}>Select an option</option>
                            <option
                                value={'PROBLEM_WITH_ORDER'}>
                                Problem with order
                            </option>
                            <option
                                value={'MISSING_OR_INACCURATE_INFORMATION'}>
                                Missing or inaccurate information
                            </option>
                            <option
                                value={'PARTS_MISMATCH'}>
                                Parts mismatch
                            </option>
                            <option
                                value={'PRICE_ISSUE'}>
                                Price issue
                            </option>
                            <option
                                value={'OFFENSIVE_CONTENT'}>
                                Offensive content
                            </option>
                            <option
                                value={'ILLEGAL_CONTENT'}>
                                Illegal content
                            </option>
                            <option
                                value={'OTHER'}>Other</option>
                        </Form.Select>
                    </Form.Group>

                    {selectedOption === 'OTHER' && (
                        <Form.Group
                            controlId={'formProductIssueOtherReason'}>
                            <Form.Label>
                                Please give us a short description about the issue.
                            </Form.Label>
                            <Form.Control
                                isInvalid={validated && (
                                    otherReason.trim().length < 1
                                    || otherReason.trim().length > 255
                                )}
                                isValid={selectedOption === 'OTHER'
                                    && otherReason.trim().length > 0
                                }
                                maxLength={255}
                                required={selectedOption === 'OTHER'}
                                type={'text'}
                                value={otherReason}
                                onChange={handleOtherReasonChange}
                            />
                            <Form.Control.Feedback
                                type={'invalid'}>
                                Please provide a short description.
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        disabled={isSubmitting}
                        variant={'secondary'}
                        onClick={handleClose}>
                        Close
                    </Button>
                    <Button
                        disabled={isSubmitting
                            || selectedOption === ''
                            || (selectedOption === 'OTHER'
                                && otherReason.trim().length < 1
                            )
                        }
                        type={'submit'}
                        variant={'primary'}>
                        Submit
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    </>);
};

ReportProductIssueModal.propTypes = {
    productId: oneOfType([number, string]),
};

export default ReportProductIssueModal;